import {
  Link,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  TableBody,
  useTheme,
  Switch,
  Box,
  Pagination,
  useMediaQuery,
} from '@mui/material';
import { Stack } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGet } from '../../api/common';
import { track } from '../../api/track';
import { ScoreCardModal } from '../../modals/ScoreCardModal';
import { Player, DetailedPlayer, DbQuestionData } from '../room/types';
import { assignRanks, utcToLocalDateTime } from '../room/utils';
import { BiGhost } from 'react-icons/bi';
import { FaSeedling } from 'react-icons/fa';
import SearchIcon from '@mui/icons-material/Search';
import { GameDb } from '@par/shared/common/types';
import {
  CATEGORY_SETS_LABEL,
  MODE_ABBR,
  MODE_DISPLAY_NAME,
} from '@par/shared/common/constants';
import { GiDevilMask, GiRaiseZombie, GiStoneTower } from 'react-icons/gi';
import BalanceIcon from '@mui/icons-material/Balance';
import { Profile } from '@par/shared/types/player';

const cacheGame: Record<string, GameDb> = {};
const cacheQuestionSet: Record<string, DbQuestionData[]> = {};

type PropTypes = {
  profile: Profile;
  viewProfile: Profile;
};

export const LatestGames = ({ profile, viewProfile }: PropTypes) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [gameRows, setGameRows] = useState<GameDb[]>();
  const [gameRow, setGameRow] = useState<GameDb>();
  const [gamesCount, setGamesCount] = useState<number>();
  const [questionSet, setQuestionSet] = useState<DbQuestionData[]>();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [dailiesToggle, setDailiesToggle] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    if (pageNum === 1 || !dailiesToggle) {
      initState();
    }
    setDailiesToggle(false);
    setPageNum(1);
  }, [viewProfile]);

  useEffect(() => {
    const offset = 10 * pageNum - 10;
    const limit = 10;
    if (dailiesToggle) {
      fetchDailiesCount(viewProfile.id);
      fetchDailies(viewProfile.id, limit, offset);
      return;
    }
    fetchGamesCount(viewProfile.id);
    fetchGames(viewProfile.id, limit, offset);
  }, [pageNum, dailiesToggle]);

  const initState = () => {
    const offset = 10 * pageNum - 10;
    const limit = 10;
    fetchGamesCount(viewProfile.id);
    fetchGames(viewProfile.id, limit, offset);
  };

  const fetchDailiesCount = async (profileId: string) => {
    const res = await fetchGet(`/games/count-dailies/${profileId}`);
    setGamesCount(res.dailiesCount);
  };

  const fetchDailies = async (profileId: string, limit = 10, offset = 0) => {
    const dailies = await fetchGet(
      `/games/dailies/${profileId}/${limit}/${offset}`
    );
    setGameRows(dailies);
  };

  const fetchGamesCount = async (profileId: string) => {
    const res = await fetchGet(`/games/count/${profileId}`);
    setGamesCount(res.gamesCount);
  };

  const fetchGames = async (profileId: string, limit = 10, offset = 0) => {
    const games = await fetchGet(
      `/games/profile/${profileId}/${limit}/${offset}`
    );
    setGameRows(games);
  };

  const getGameScores = (data: GameDb) => {
    if (
      data &&
      data.players &&
      (data.mode === 'solo' || data.mode === 'sit-n-go')
    ) {
      const playerMe = data.players[viewProfile.id];
      const myScore = playerMe?.parScore;
      if (myScore === undefined) return '';
      return `${myScore > 0 ? '+' + myScore : myScore}`;
    }
    if (data.mode !== 'solo') {
      const playerMe = data.players[viewProfile.id];
      const myScore = playerMe?.parScore;
      if (!myScore === undefined) return '';
      const playerThemProfileId = Object.keys(data.players).find(
        (profileId) => profileId !== viewProfile.id
      );
      const playerThem = playerThemProfileId
        ? data.players[playerThemProfileId]
        : undefined;
      const theirScore = playerThem?.parScore;
      if (theirScore === undefined) return '';
      return `${myScore > 0 ? '+' + myScore : myScore} to ${
        theirScore > 0 ? '+' + theirScore : theirScore
      }`;
    }
  };

  const handleUsernameClick = async (e: any, player: Player) => {
    e.stopPropagation();
    await track(
      profile.id,
      'profile.games.table.username.link',
      player.profileId
    );
    navigate(`/profile/overview/${player.profileId}`);
  };

  const getGameResult = (game: GameDb) => {
    if (game.mode === 'solo') return '--';
    if (game.mode === 'match') {
      if (game.tie) return 'Tie';
      if (game.winner === viewProfile?.id) {
        return 'W';
      } else {
        return 'L';
      }
    }
    if (game.mode === 'sit-n-go') {
      return game.sitAndGoResult;
    }
  };

  const handlePaginateOnChange = (event: any, pageNum: number) => {
    setPageNum(pageNum);
  };

  const stackName = (r: GameDb) => (
    <Tooltip placement="top" title={`Game ID: ${r.gameId}`}>
      <Stack>
        <Stack direction="row" spacing={1}>
          {r.ranked ? (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: theme.palette.ranked.main,
              }}
            >
              Ranked
            </Typography>
          ) : null}
          {r.ghostChallenge ? (
            <BiGhost
              style={{
                fontSize: '18px',
                color: theme.palette.warning.main,
              }}
            />
          ) : null}
          {r.matchMaking ? (
            <Tooltip title="This game was Searched" placement="bottom">
              <SearchIcon
                style={{
                  fontSize: '22px',
                  color: theme.palette.dailies.main,
                }}
              />
            </Tooltip>
          ) : null}
          {r.handicap ? (
            <Tooltip
              title="A handicap was set for this game"
              placement="bottom"
            >
              <Box
                sx={{
                  marginLeft: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <BalanceIcon
                  style={{
                    fontSize: '20px',
                    color: theme.palette.handicap.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
          {r.variant === 'survival' && r.survivalMode === 'endurance' ? (
            <Tooltip title="Survival - Endurance" placement="bottom">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GiRaiseZombie
                  style={{
                    fontSize: '20px',
                    color: theme.palette.survival.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
          {r.variant === 'survival' && r.survivalMode === 'challenge' ? (
            <Tooltip title="Survival - Challenge Tower" placement="bottom">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GiStoneTower
                  style={{
                    fontSize: '16px',
                    color: theme.palette.survival.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
          {r.variant === 'survival' && r.survivalMode === 'demon' ? (
            <Tooltip title="Survival - Demons" placement="bottom">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GiDevilMask
                  style={{
                    fontSize: '18px',
                    color: theme.palette.demon.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
        </Stack>
        {r.customGameName || 'N/A'}
      </Stack>
    </Tooltip>
  );

  const getRatingNextToUsername = (player: Player) => {
    const formatRating = (rating: number) => {
      const rounded = Math.round(rating);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    };
    return formatRating(player.rankedRatingDiff || 0);
  };

  const ratingColor = (player: Player) => {
    if (Math.round(player.rankedRatingDiff || 0) > 0)
      return theme.palette.success.main;
    if (Math.round(player.rankedRatingDiff || 0) < 0)
      return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const handleRowClick = async (row: any) => {
    await track(profile.id, 'profile.games.table.row', row.gameId);
    let game;
    let questionSet;
    if (cacheGame[row.gameId] && cacheQuestionSet[row.gameId]) {
      game = cacheGame[row.gameId];
      questionSet = cacheQuestionSet[row.gameId];
    } else {
      const res = await fetchGet(`/games/result/${row.gameId || row._id}`);
      game = res.game;
      questionSet = res.questionSet;
      if (row.gameId) {
        cacheGame[row.gameId] = game;
        cacheQuestionSet[row.gameId] = questionSet;
      }
    }
    setGameRow(game);
    setQuestionSet(questionSet);
    setOpenScorecardModal(true);
  };

  const handleViewResultsClick = async (game: GameDb) => {
    const res = await fetchGet(`/games/result/${game.gameId}`);
    setGameRow(res.game);
    setQuestionSet(res.questionSet);
    setOpenScorecardModal(true);
  };

  const results = (game: GameDb) => (
    <Stack>
      <Typography variant="body2">
        {utcToLocalDateTime(game.createdAt)}
      </Typography>
      <Link
        sx={{ cursor: 'pointer' }}
        onClick={() => handleViewResultsClick(game)}
      >
        View Results
      </Link>
    </Stack>
  );

  const getJoinedPlayers = (game: GameDb) => {
    if (!game.players || Object.keys(game.players).length === 0) return '--';
    const rankedPlayers = assignRanks(
      Object.values(game.players).sort((a, b) => a.parScore - b.parScore)
    );
    return (
      <Stack width={'100%'}>
        {rankedPlayers.map((p, i) => (
          <Link
            key={i}
            component="button"
            variant="body2"
            onClick={(e) => handleUsernameClick(e, p)}
            textAlign={'left'}
          >
            <Stack direction={'row'} sx={{ whiteSpace: 'nowrap' }}>
              {game.mode === 'sit-n-go' ? `${p.rank}.` : ''} {p.username}
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: ratingColor(p) }}
                  >
                    {getRatingNextToUsername(p)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                </Stack>
              ) : null}
              {game.matchMaking && game.matchMakingSeededBy === p.profileId ? (
                <Tooltip title="Seeded this match" placement="top">
                  <Box
                    sx={{
                      marginLeft: 1,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FaSeedling
                      style={{
                        fontSize: '14px',
                        color: theme.palette.success.main,
                      }}
                    />
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>
          </Link>
        ))}
      </Stack>
    );
  };

  const createOptionsLabel = (game: GameDb) => {
    if (game.daily) {
      return 'Daily';
    }
    if (game.demo) {
      return 'Demo';
    }
    if (game.starterKit) {
      return `${game.starterKit.starterKitName} - ${game.starterKit.order} - ${game.starterKit.clue}`;
    }
    return `${MODE_ABBR[game.mode]} - ${
      game.gameType === 'casual' ? 'Casual' : 'Pro'
    } - ${
      game.categorySet && game.categorySet !== 'none'
        ? CATEGORY_SETS_LABEL[game.categorySet]
        : !game.ranked &&
          game.variant === 'normal' &&
          game.categorySelections?.length
        ? '[' + game.categorySelections.join(' ') + ']'
        : CATEGORY_SETS_LABEL[game.categorySet]
    }`;
  };

  return (
    <>
      {gameRow && questionSet ? (
        <ScoreCardModal
          onClose={() => setOpenScorecardModal(false)}
          categorySet={gameRow.categorySet}
          demons={gameRow.demons}
          demonsVanquished={gameRow.demonsVanquished}
          gameId={gameRow.gameId}
          gameType={gameRow.gameType as 'casual' | 'pro'}
          handicap={gameRow.handicap}
          isDaily={gameRow.daily}
          league={gameRow.league}
          openScordcardModal={openScorecardModal}
          mode={gameRow.mode}
          players={gameRow.players as Record<string, DetailedPlayer>}
          profileId={viewProfile.id}
          questionSet={questionSet}
          ranked={gameRow.ranked}
          teams={gameRow.teams}
          seededBy={gameRow.matchMakingSeededBy}
          starterKit={gameRow.starterKit}
          survivalMode={gameRow.survivalMode}
          variant={gameRow.variant}
        />
      ) : null}
      <Typography
        variant="h5"
        sx={{
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 0px ${theme.palette.secondary.main}, 0 0 0px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Latest Games
      </Typography>
      <Stack direction={'row'} paddingBottom={1}>
        <Box sx={{ width: '50%' }}>
          {profile.id === viewProfile.id || profile.admin ? (
            <Box
              sx={{
                color: 'dailies.main',
                paddingLeft: 2,
                textAlign: 'left',
              }}
            >
              Dailies
              <Switch
                checked={dailiesToggle}
                onChange={() => setDailiesToggle((prev) => !prev)}
              />
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            justifyContent: 'right',
          }}
        >
          {gamesCount ? (
            <Pagination
              count={Math.ceil(gamesCount / 10)}
              size={xsBreakpoint ? 'small' : 'medium'}
              onChange={handlePaginateOnChange}
              page={pageNum}
              sx={{ display: 'flex', justifyContent: 'right' }}
            />
          ) : null}
        </Box>
      </Stack>

      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell width={'15%'}>Name</TableCell>
              <TableCell width={'15%'}>Options</TableCell>
              <TableCell width={'3%'}>Result</TableCell>
              <TableCell width={'8%'}>Players</TableCell>
              <TableCell width={'10%'} style={{ textAlign: 'center' }}>
                Scores
              </TableCell>
              <TableCell width={'10%'}>Results</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gameRows?.map((row: GameDb) => (
              <TableRow
                hover
                key={row._id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                // onClick={() => handleRowClick(row)}
                // sx={{ cursor: 'pointer' }}
              >
                <TableCell>{stackName(row)}</TableCell>
                <TableCell>{createOptionsLabel(row)}</TableCell>
                <TableCell>{getGameResult(row)}</TableCell>
                <TableCell>{getJoinedPlayers(row)}</TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  {getGameScores(row)}
                </TableCell>
                <TableCell>{results(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
